import { useState } from "react";
import { useResource, formatDateTime, titleCase } from "../../lib/hooks";
import { useAuth } from "../../context/AuthContext";
import { FilterBar, matches } from "../../components/FilterBar";
import { EntityForm, Field } from "../../components/EntityForm";
import { InitialsAvatar } from "../../components/InitialsAvatar";
import { companyCreateFields, contactCreateFields, contactOptionsOf, personLabel } from "../../lib/formOptions";
import { QuoteEditor } from "../../components/QuoteEditor";

interface Inquiry {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string | null;
    organisation: string | null;
    linked_company_id: string | null;
    contact_id: string | null;
    role_title: string | null;
    suburb: string | null;
    inquiry_type: string | null;
    services: string[];
    message: string;
    status: "new" | "contacted" | "converted" | "closed";
    created_at: string;
}

const STATUS = ["new", "contacted", "converted", "closed"].map((s) => ({ value: s, label: titleCase(s) }));

const statusChip = (s: Inquiry["status"]) =>
    s === "new" ? "warn" : s === "converted" ? "pass" : s === "closed" ? "mute" : "info";

export const Inquiries = () => {
    const { user } = useAuth();
    const canEdit = user?.role === "admin" || user?.role === "manager";

    const inquiries = useResource<Inquiry[]>("/inquiries");
    const companies = useResource<Array<{ id: string; name: string }>>("/companies");
    const contacts = useResource<Array<{ id: string; first_name: string; last_name: string; company_id: string | null }>>("/contacts");
    const serviceCatalogue = useResource<Array<{ name: string; default_rate: string | null }>>("/services");
    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<Record<string, string>>({});
    const [dialog, setDialog] = useState<{ kind: "create" } | { kind: "edit"; inquiry: Inquiry } | null>(null);
    const [expanded, setExpanded] = useState<string | null>(null);
    const [quoteFor, setQuoteFor] = useState<Inquiry | null>(null);

    const serviceOptions = (serviceCatalogue.data ?? []).map((s) => ({ value: s.name, label: s.name }));
    const fields: Field[] = [
        { name: "first_name", label: "First name", kind: "text", required: true, half: true },
        { name: "last_name", label: "Last name", kind: "text", required: true, half: true },
        { name: "email", label: "Email", kind: "email", required: true, half: true },
        { name: "phone", label: "Phone", kind: "tel", half: true },
        { name: "role_title", label: "Your role", kind: "select", half: true,
          optionsFrom: { path: "/inquiry-roles", addLabel: "Add role" } },
        { name: "suburb", label: "Suburb", kind: "text", half: true },
        { name: "organisation", label: "Organisation (free text)", kind: "text", half: true },
        { name: "linked_company_id", label: "Company (CRM record)", kind: "select", half: true,
          options: (companies.data ?? []).map((c) => ({ value: c.id, label: c.name })),
          addNew: { title: "New organisation", path: "/companies", fields: companyCreateFields() } },
        { name: "contact_id", label: "Contact", kind: "select", half: true,
          cascadeFrom: "linked_company_id",
          options: contactOptionsOf(contacts.data ?? []),
          addNew: { title: "New contact", path: "/contacts",
              fields: contactCreateFields((companies.data ?? []).map((c) => ({ value: c.id, label: c.name }))),
              optionLabel: personLabel,
              inherit: { company_id: "linked_company_id" } } },
        { name: "inquiry_type", label: "Enquiry type", kind: "select", half: true,
          optionsFrom: { path: "/inquiry-types", addLabel: "Add type" } },
        { name: "services", label: "Services of interest", kind: "multiselect", options: serviceOptions },
        { name: "status", label: "Status", kind: "select", options: STATUS, half: true },
        { name: "message", label: "Message", kind: "textarea", required: true },
    ];

    const all = inquiries.data ?? [];
    const filtered = all.filter((i) =>
        matches(search, `${i.first_name} ${i.last_name}`, i.email, i.organisation, i.message) &&
        (!filters.status || i.status === filters.status)
    );

    if (inquiries.error) return <div className="error-banner">{inquiries.error}</div>;

    return (
        <div>
            {dialog?.kind === "create" && (
                <EntityForm title="Log enquiry" path="/inquiries" fields={fields}
                    onClose={() => setDialog(null)} onSaved={() => { setDialog(null); inquiries.reload(); }} />
            )}
            {dialog?.kind === "edit" && (
                <EntityForm title="Update enquiry" path="/inquiries" fields={fields} initial={dialog.inquiry}
                    onClose={() => setDialog(null)} onSaved={() => { setDialog(null); inquiries.reload(); }} />
            )}
            {quoteFor && (
                <QuoteEditor
                    quote={{
                        inquiry_id: quoteFor.id,
                        company_id: quoteFor.linked_company_id,
                        contact_id: quoteFor.contact_id,
                        notes: quoteFor.message,
                        apply_gst: false,
                    }}
                    prefillLines={(quoteFor.services ?? []).map((name) => {
                        const svc = (serviceCatalogue.data ?? []).find((s) => s.name === name);
                        return { service: name, hours: "1", rate: svc?.default_rate != null ? String(Number(svc.default_rate)) : "" };
                    })}
                    onClose={() => setQuoteFor(null)}
                    onSaved={() => { setQuoteFor(null); inquiries.reload(); }}
                />
            )}

            <FilterBar
                search={search} onSearch={setSearch}
                searchPlaceholder="Search enquiries…"
                values={filters}
                onChange={(name, value) => setFilters((f) => ({ ...f, [name]: value }))}
                filters={[{ name: "status", allLabel: "All Status", options: STATUS }]}
                action={canEdit && <button className="btn" onClick={() => setDialog({ kind: "create" })}>+ Log enquiry</button>}
            />

            <div className="card">
                <div className="card-body flush">
                    <div className="table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>From</th><th>Organisation</th><th>Type</th>
                                    <th>Received</th><th>Status</th>
                                    {canEdit && <th style={{ textAlign: "right" }}>Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((inquiry) => (
                                    <FragmentRow key={inquiry.id}
                                        inquiry={inquiry} canEdit={canEdit}
                                        expanded={expanded === inquiry.id}
                                        onToggle={() => setExpanded(expanded === inquiry.id ? null : inquiry.id)}
                                        onEdit={() => setDialog({ kind: "edit", inquiry })}
                                        onCreateQuote={() => setQuoteFor(inquiry)} />
                                ))}
                                {filtered.length === 0 && (
                                    <tr><td colSpan={canEdit ? 6 : 5} className="empty">No enquiries match.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

const FragmentRow = ({
    inquiry, canEdit, expanded, onToggle, onEdit, onCreateQuote,
}: {
    inquiry: Inquiry; canEdit: boolean; expanded: boolean;
    onToggle: () => void; onEdit: () => void; onCreateQuote: () => void;
}) => (
    <>
        <tr className="clickable" onClick={onToggle}>
            <td>
                <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                    <InitialsAvatar name={`${inquiry.first_name} ${inquiry.last_name}`} size={28} />
                    <div>
                        <div style={{ fontWeight: 600 }}>{inquiry.first_name} {inquiry.last_name}</div>
                        <div style={{ fontSize: "0.75rem", color: "var(--muted)" }}>{inquiry.email}</div>
                    </div>
                </div>
            </td>
            <td>{inquiry.organisation ?? "—"}</td>
            <td>{inquiry.inquiry_type ?? "—"}</td>
            <td className="mono">{formatDateTime(inquiry.created_at)}</td>
            <td><span className={`chip ${statusChip(inquiry.status)}`}>{titleCase(inquiry.status)}</span></td>
            {canEdit && (
                <td onClick={(e) => e.stopPropagation()}>
                    <div className="row-actions">
                        <button className="icon-btn" onClick={onEdit}>Update</button>
                        <button className="icon-btn" onClick={onCreateQuote}>Create quote</button>
                    </div>
                </td>
            )}
        </tr>
        {expanded && (
            <tr>
                <td colSpan={canEdit ? 6 : 5} style={{ background: "var(--surface-sunk)" }}>
                    <p style={{ fontSize: "0.85rem", lineHeight: 1.55 }}>{inquiry.message}</p>
                    {inquiry.services.length > 0 && (
                        <p style={{ marginTop: "0.4rem" }}>
                            {inquiry.services.map((s) => (
                                <span key={s} className="chip info" style={{ marginRight: 4 }}>{s}</span>
                            ))}
                        </p>
                    )}
                </td>
            </tr>
        )}
    </>
);
